import { snowFraction, temperatureAt, type TemperatureProfile } from './temperature.ts';

/**
 * Storm precipitation: how much falls where, and as what.
 *
 * The user gives new snow depth measured at a reference elevation. That is converted to a
 * precipitation amount (water equivalent) at the reference elevation, scaled with elevation by
 * an orographic gradient, split into snow and rain by air temperature, and the snow part turned
 * back into depth with a temperature- and wind-dependent new-snow density.
 */

/**
 * Density of new snow (kg/m³) from air temperature (°C), after Hedstrom & Pomeroy (1998) for
 * T ≤ 0 and Pomeroy & Gray (1995) above freezing, plus wind packing.
 *
 * Wind packing is a simple linear term (+8 kg/m³ per m/s above 4 m/s): falling snow in wind is
 * broken up and packed. Capped at 250 kg/m³ — anything denser is wind slab, handled by drift.
 */
export function newSnowDensity(airTemp: number, windSpeed = 0): number {
  const base =
    airTemp <= 0 ? 67.92 + 51.25 * Math.exp(airTemp / 2.59) : 119.17 + 20 * Math.sqrt(airTemp);
  const wind = Math.max(0, windSpeed - 4) * 8;
  return Math.min(250, base + wind);
}

/** Density (kg/m³) of wind-deposited snow (wind slab) for a given reference wind speed. */
export function windSlabDensity(windSpeed: number): number {
  return Math.min(450, Math.max(220, 200 + 10 * windSpeed));
}

export interface StormPrecipitation {
  /** New snow depth (cm) as measured at `referenceElevation` at the end of the storm. */
  newSnowCm: number;
  referenceElevation: number;
  /** Precipitation increase per 100 m of elevation (%). */
  orographicPctPer100m: number;
  /**
   * Measured precipitation (mm water) at the reference elevation, from imported data — rain
   * included, which `newSnowCm` can't express. Used while `newSnowCm` still equals the value it
   * was derived with, so editing the new snow switches back to the conversion.
   */
  measured?: { precipitationMm: number; newSnowCm: number };
}

/**
 * Minimum snow fraction assumed at the reference elevation when converting the observed new snow
 * depth back to precipitation — keeps the conversion finite when the reference sits in rain.
 */
const MIN_REFERENCE_SNOW_FRACTION = 0.2;

/** Total precipitation (kg/m² = mm water) at the reference elevation over the storm. */
export function referencePrecipitation(
  p: StormPrecipitation,
  temperature: TemperatureProfile,
  windSpeed: number,
): number {
  if (p.measured && Math.abs(p.measured.newSnowCm - p.newSnowCm) < 1e-6)
    return p.measured.precipitationMm;
  const t = temperatureAt(temperature, p.referenceElevation);
  const swe = (p.newSnowCm / 100) * newSnowDensity(t, windSpeed);
  return swe / Math.max(MIN_REFERENCE_SNOW_FRACTION, snowFraction(t));
}

/** Total precipitation (kg/m²) at an elevation, from the reference amount. */
export function precipitationAt(
  p: StormPrecipitation,
  referenceAmount: number,
  elevation: number,
): number {
  const factor = 1 + (p.orographicPctPer100m / 100) * ((elevation - p.referenceElevation) / 100);
  return referenceAmount * Math.max(0, factor);
}
